import React, { useEffect, useState, useRef } from "react";

import { Container, Paper, Tabs, Tab, MenuItem, Box, styled, Typography, Select, FormControl, InputLabel, Grid, Button } from "@mui/material";

import DashboardIcon from "@mui/icons-material/Dashboard";
import Filter1Icon from "@mui/icons-material/Filter1";
import Filter2Icon from "@mui/icons-material/Filter2";
import Filter3Icon from "@mui/icons-material/Filter3";
import SearchIcon from "@mui/icons-material/Search";

import OverviewPanel from "./overviewPanel";
import { getDashboardCost, getDashboardOvertime, getDashboardOverview, getDashboardWorkingtime } from "../../../../actions/dashboard";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import OvertimePanel from "./overtimePanel";
import WorkingtimePanel from "./workingTimePanel";
import CostPanel from "./costPanel";
import { getAllDepartments } from "../../../../actions/department";
import ExportButton from "../../shared/dashboard/export/ExportButton";
import { getAllUsersCompany } from "../../../../actions/user";
import { getEmployWorkingDays, getResignEmployee } from "../../../../actions/report";
import { getOvertime36HoursList } from "../../../../actions/requestTime";

const StyledRoot = styled(Box)({
  "& .tabs-container": {
    marginBottom: "24px",
  }
})

const TabsStyled = styled(Tabs)({
  width: "fit-content",
  maxWidth: "100%",
  boxShadow:
    "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
  borderRadius: "16px",
  "& .tab": {
    backgroundColor: "#FFFFFF",
    height: "fit-content",
  },
  "& .TabBorderRight": {
    borderRight: "1px solid rgba(0, 0, 0, 0.1)",
  },
  "& span": {
    display: "none",
  },
  "& .Mui-selected": {
    backgroundColor: "#007afe",
    color: "white !important",
    border: "1px solid #007afe",
  },
});

const Overview = () => {

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [month, setMonth] = useState(dayjs().get("month") + 1);
  const [year, setYear] = useState(dayjs().get("year"));
  const [tabValue, setTabValue] = useState("overviewAdmin");

  const [department, setDepartment] = useState("");
  const [section, setSection] = useState("");
  const [appliedByTab, setAppliedByTab] = useState({});

  const { result: dashboardOverview } = useSelector(state => state.dashboardOverview);
  const { result: dashboardOvertime } = useSelector(state => state.dashboardOvertime);
  const { result: dashboardWorkingtime } = useSelector(state => state.dashboardWorkingtime);
  const { result: dashboardCost } = useSelector(state => state.dashboardCost);
  const { result: userProfile } = useSelector(state => state.userProfile);
  const { result: departmentStore } = useSelector(state => state.department);
  const { result: vendorUsers } = useSelector((state) => state.users); // active/terminate ของบริษัท admin
  const idCompany = userProfile && userProfile.idCompany;

  const [leaveAbsentLate, setLeaveAbsentLate] = useState(null);
  const leaveKeyRef = useRef(null);

  // ===== ข้อมูลจริง (ชุดเดียวกับ vendor — แต่ admin = บริษัทเดียว ไม่มี loop) =====
  const empPassesFilter = (e, f) => {
    if (!e) return false;
    if (f.section && departmentStore) {
      let secName = null;
      departmentStore.forEach((d) =>
        (d.sections || []).forEach((s) => {
          if (String(s.idSection) === String(f.section)) secName = s.sectionName;
        })
      );
      if (secName && e.sectionName !== secName) return false;
    } else if (f.department && departmentStore) {
      const dep = departmentStore.find((d) => String(d.idDepartment) === String(f.department));
      const secNames = dep ? (dep.sections || []).map((s) => s.sectionName) : [];
      if (secNames.length && !secNames.includes(e.sectionName)) return false;
    }
    return true;
  };

  const activeEmpMap = React.useMemo(() => {
    const m = new Map();
    ((vendorUsers && vendorUsers.active) || []).forEach((e) => m.set(e.idEmp, e));
    return m;
  }, [vendorUsers]);

  const rosterAtMonthEnd = (f) => {
    if (!f || !vendorUsers) return [];
    const monthEnd = dayjs()
      .year(Number(f.year))
      .month(Number(f.month) - 1)
      .endOf("month");
    const roster = [
      ...(vendorUsers.active || []),
      ...((vendorUsers.termainate || vendorUsers.terminate) || []),
    ];
    const seen = new Set();
    return roster.filter((e) => {
      if (!e || seen.has(e.idEmp)) return false;
      if (!empPassesFilter(e, f)) return false;
      if (e.hiringDate && dayjs(e.hiringDate).isAfter(monthEnd, "day")) return false;
      if (e.resignDate && dayjs(e.resignDate).isBefore(monthEnd, "day")) return false;
      seen.add(e.idEmp);
      return true;
    });
  };

  const computeRealEmployee = (f) => {
    if (!f || !vendorUsers) return null;
    let list = rosterAtMonthEnd(f);
    const male = list.filter((e) => e.gender === "ชาย").length;
    const female = list.filter((e) => e.gender === "หญิง").length;
    const salaryTotal = list.reduce((a, e) => {
      const v = Number(e.salaryValue) || 0;
      const isDaily = String(e.paymentTypeName || "").includes("วัน");
      return a + (isDaily ? v * 30 : v);
    }, 0);
    const newIn = list.filter(
      (e) => e.hiringDate && dayjs(e.hiringDate).month() + 1 === Number(f.month) && dayjs(e.hiringDate).year() === Number(f.year)
    ).length;
    return { employeeTotal: list.length, maleTotal: male, femaleTotal: female, salaryTotal, employeeInTotal: newIn };
  };

  // จำนวนพนักงานตามแผนก/ส่วน (active, กรองแล้ว) — สำหรับโดนัทในแท็บ OT
  const computeHeadcountByDept = (f) => {
    if (!f || !vendorUsers) return [];
    const m = {};
    rosterAtMonthEnd(f).forEach((e) => {
      const k = e.sectionName || "ไม่ระบุ";
      m[k] = (m[k] || 0) + 1;
    });
    return Object.keys(m).map((name) => ({ name, count: m[name] })).sort((a, b) => b.count - a.count);
  };

  const computeHeadcountTrend = (f) => {
    if (!f || !vendorUsers) return [];
    const now = dayjs();
    const isCurrentYear = Number(f.year) === now.year();
    const maxMonth = isCurrentYear ? now.month() + 1 : 12;
    const out = [];
    for (let m = 1; m <= maxMonth; m++) {
      out.push({
        x: dayjs().month(m - 1).format("MMM"),
        y: rosterAtMonthEnd({ ...f, month: m }).length,
      });
    }
    return out;
  };

  const sumLeaveUsed = (lu) =>
    lu && typeof lu === "object" ? Object.values(lu).reduce((a, v) => a + (Number(v) || 0), 0) : 0;

  const computeRealWorkingTime = (f) => {
    if (!f || !leaveAbsentLate || !leaveAbsentLate.employeeList || !vendorUsers) return null;
    const wdMap = new Map();
    leaveAbsentLate.employeeList.forEach((e) => wdMap.set(e.idEmp, e));
    const rows = (vendorUsers.active || [])
      .filter((emp) => empPassesFilter(emp, f))
      .map((emp) => {
        const wd = wdMap.get(emp.idEmp) || {};
        return {
          idEmp: emp.idEmp,
          firstname: emp.firstname,
          lastname: emp.lastname,
          imageProfile: emp.imageProfile || null,
          arrive: Number(wd.arrive) || 0,
          absent: Number(wd.absent) || 0,
          late: Number(wd.late) || 0,
          earlyBack: Number(wd.earlyBack) || 0,
          leave: sumLeaveUsed(wd.leaveUsed),
        };
      });
    return {
      absentTotal: rows.reduce((a, r) => a + r.absent, 0),
      lateTotal: rows.reduce((a, r) => a + r.late, 0),
      earlyTotal: rows.reduce((a, r) => a + r.earlyBack, 0),
      leaveTotal: rows.reduce((a, r) => a + r.leave, 0),
      top5Absent: rows.filter((r) => r.absent > 0).sort((a, b) => b.absent - a.absent).slice(0, 5),
      top5Late: rows.filter((r) => r.late > 0).sort((a, b) => b.late - a.late).slice(0, 5),
      rows,
    };
  };

  const computeWorkdayRows = (f) => {
    const rwt = computeRealWorkingTime(f);
    if (!rwt) return null;
    return rwt.rows
      .slice()
      .sort((a, b) => b.absent + b.late + b.earlyBack + b.leave - (a.absent + a.late + a.earlyBack + a.leave));
  };

  const computeWorkdayTop5Absent = (f) => {
    const rwt = computeRealWorkingTime(f);
    if (!rwt) return null;
    return rwt.top5Absent.map((r) => ({ ...r, absentTotal: r.absent }));
  };

  const computeLeaveByType = (f) => {
    if (!leaveAbsentLate || !leaveAbsentLate.employeeList) return [];
    const nameMap = {};
    (leaveAbsentLate.leaveList || []).forEach((l) => {
      nameMap[l.idLeaveGroup] = l.leaveGroupName;
    });
    const agg = {};
    leaveAbsentLate.employeeList
      .filter((e) => activeEmpMap.has(e.idEmp))
      .filter((e) => empPassesFilter(activeEmpMap.get(e.idEmp), f))
      .forEach((e) => {
        const lu = e.leaveUsed || {};
        Object.keys(lu).forEach((gid) => {
          agg[gid] = (agg[gid] || 0) + (Number(lu[gid]) || 0);
        });
      });
    return Object.keys(agg)
      .filter((gid) => agg[gid] > 0)
      .map((gid) => ({ name: nameMap[gid] || "อื่นๆ", value: agg[gid] }))
      .sort((a, b) => b.value - a.value);
  };

  const computeNewEmployees = (f) => {
    if (!f || !vendorUsers) return null;
    return rosterAtMonthEnd(f)
      .filter((e) => e.hiringDate && dayjs(e.hiringDate).month() + 1 === Number(f.month) && dayjs(e.hiringDate).year() === Number(f.year))
      .map((e) => ({ firstname: e.firstname, lastname: e.lastname, positionName: e.positionsName, imageProfile: e.imageProfile || null, dateText: dayjs(e.hiringDate).format("D MMMM YYYY") }));
  };

  const weeksOfMonth = (m, y) => {
    const first = dayjs().year(y).month(m - 1).date(1);
    const last = first.endOf("month");
    let cur = first.startOf("week");
    cur = cur.day() === 0 ? cur.add(1, "day") : cur.startOf("week").add(1, "day");
    const weeks = [];
    let guard = 0;
    while (cur.isBefore(last) && guard++ < 8) {
      const end = cur.add(6, "day");
      weeks.push({ start: cur.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD"), label: `${cur.format("D MMM")} - ${end.format("D MMM")}` });
      cur = cur.add(7, "day");
    }
    return weeks;
  };

  const fetchOT36List = async (f) => {
    if (!idCompany) return [];
    const weeks = weeksOfMonth(f.month, f.year);
    const out = [];
    for (const w of weeks) {
      let r = [];
      try {
        r = await getOvertime36HoursList({ idCompany, start: w.start, end: w.end });
      } catch (e) {}
      const emps = (Array.isArray(r) ? r : [])
        .filter((x) => x && Number(x.amountHours) > 36 && activeEmpMap.has(x.idEmp)) // "เกิน 36" = มากกว่า 36 (ไม่นับ 36.00 พอดี)
        .filter((x) => empPassesFilter(activeEmpMap.get(x.idEmp), f))
        .map((x) => {
          const emp = activeEmpMap.get(x.idEmp) || {};
          return { ...x, imageProfile: emp.imageProfile, employeeId: x.employeeId || emp.employeeId || emp.employeeTypeEmployeeId };
        });
      out.push({ ...w, employees: emps });
    }
    return out;
  };

  const workdayQuery = (f, cid) => {
    const s = dayjs().year(Number(f.year)).month(Number(f.month) - 1).date(1);
    return {
      idCompany: cid,
      start: s.format("YYYY-MM-DD"),
      end: s.endOf("month").format("YYYY-MM-DD"),
      getAll: true,
    };
  };

  const mapLimit = async (items, limit, fn) => {
    const ret = [];
    let i = 0;
    const workers = Array.from({ length: Math.min(limit, items.length || 1) }, async () => {
      while (i < items.length) {
        const idx = i++;
        ret[idx] = await fn(items[idx], idx);
      }
    });
    await Promise.all(workers);
    return ret;
  };

  const companyIdsFor = () => {
    const fromEmp = [
      ...new Set((((vendorUsers && vendorUsers.active) || []).map((e) => e.idCompany)).filter(Boolean)),
    ];
    if (fromEmp.length) return fromEmp;
    return idCompany ? [idCompany] : [];
  };

  const fetchTab = (tab, f) => {
    const base = {
      mode: "admin",
      month: f.month,
      year: f.year,
      idDepartment: f.department ? f.department : undefined,
      idSection: f.section ? f.section : undefined,
    };
    if (tab === "overviewAdmin") {
      dispatch(getDashboardOverview(base));
      dispatch(getDashboardCost(base)); // ต้นทุนจริง (PayrunDetail) สำหรับการ์ดเงินเดือน/OT
    } else if (tab === "dashboardOT") {
      dispatch(getDashboardOvertime(base));
      dispatch(getDashboardCost(base));
    } else if (tab === "dashboardWorkingTime") {
      dispatch(getDashboardWorkingtime(base));
    } else if (tab === "dashboardCost") {
      dispatch(getDashboardCost(base));
    }
  };

  const handleSearch = () => {
    const f = { department, section, month, year };
    setAppliedByTab((prev) => ({ ...prev, [tabValue]: f }));
    fetchTab(tabValue, f);
  };

  const handleChangeTab = (newTab) => {
    setTabValue(newTab);
    const f = appliedByTab[newTab];
    if (f) {
      setDepartment(f.department);
      setSection(f.section);
      setMonth(f.month);
      setYear(f.year);
    }
  };

  useEffect(() => {
    if (userProfile) {
      dispatch(getAllDepartments({ idCompany: userProfile.idCompany }))
      dispatch(getAllUsersCompany(userProfile.idCompany, "all")); // active/terminate ของบริษัท
    }
  }, [userProfile])

  useEffect(() => {
    const onTab = tabValue === "overviewAdmin" || tabValue === "dashboardWorkingTime";
    const f = tabValue === "overviewAdmin" ? appliedByTab.overviewAdmin : appliedByTab.dashboardWorkingTime;
    if (!onTab || !f || !vendorUsers) return;
    const companyIds = companyIdsFor();
    if (!companyIds.length) return;
    const key = JSON.stringify({ m: f.month, y: f.year, c: companyIds });
    if (leaveKeyRef.current === key) return;
    leaveKeyRef.current = key;
    let cancelled = false;
    setLeaveAbsentLate(null);
    (async () => {
      try {
        const results = await mapLimit(companyIds, 3, (cid) =>
          getEmployWorkingDays(workdayQuery(f, cid))
            .then((r) => (r && r.data ? r.data : null))
            .catch(() => null)
        );
        let employeeList = [];
        const leaveList = [];
        const seen = new Set();
        results.filter(Boolean).forEach((d) => {
          if (d.employeeList) employeeList = employeeList.concat(d.employeeList);
          (d.leaveList || []).forEach((l) => {
            if (!seen.has(l.idLeaveGroup)) {
              seen.add(l.idLeaveGroup);
              leaveList.push(l);
            }
          });
        });
        if (!cancelled) setLeaveAbsentLate({ employeeList, leaveList });
      } catch (e) {
        if (!cancelled) setLeaveAbsentLate(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, appliedByTab.overviewAdmin, appliedByTab.dashboardWorkingTime, idCompany, vendorUsers]);

  const [resignList, setResignList] = useState(null);
  const resignKeyRef = useRef(null);
  useEffect(() => {
    if (tabValue !== "overviewAdmin" || !appliedByTab.overviewAdmin) return;
    const f = appliedByTab.overviewAdmin;
    const key = JSON.stringify({ c: idCompany, d: f.department, s: f.section, m: f.month, y: f.year });
    if (resignKeyRef.current === key) return;
    resignKeyRef.current = key;
    const ym = `${f.year}-${String(f.month).padStart(2, "0")}`;
    let cancelled = false;
    setResignList(null);
    const imgMap = {};
    [
      ...((vendorUsers && vendorUsers.active) || []),
      ...((vendorUsers && (vendorUsers.termainate || vendorUsers.terminate)) || []),
    ].forEach((e) => {
      if (e.employeeId && e.imageProfile) imgMap[e.employeeId] = e.imageProfile;
    });
    (async () => {
      try {
        const q = idCompany
          ? { idCompany, month: ym, year: String(f.year) }
          : { month: ym, year: String(f.year) };
        const rs = await getResignEmployee(q);
        const items = (rs && Array.isArray(rs.data) ? rs.data : [])
          .filter(
            (x) =>
              (!f.section || String(x.idSection) === String(f.section)) &&
              (!f.department || String(x.idDepartment) === String(f.department))
          )
          .map((e) => ({
            firstname: e.firstname,
            lastname: e.lastname,
            positionName: e.positionsName,
            imageProfile: e.imageProfile || imgMap[e.employeeId] || null,
            dateText: e.resignDate ? dayjs(e.resignDate).locale(localStorage.getItem("language") || "th").format(localStorage.getItem("language") === "en" ? "D MMMM YYYY" : "D MMMM BBBB") : "",
          }));
        if (!cancelled) setResignList(items);
      } catch (e) {
        if (!cancelled) setResignList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, appliedByTab.overviewAdmin, idCompany]);


  const contentRef = useRef(null);
  const tabLabels = {
    overviewAdmin: "มุมมองภาพรวม",
    dashboardOT: "ข้อมูลค่าล่วงเวลา",
    dashboardWorkingTime: "ภาพรวมเวลาทำงาน",
    dashboardCost: "ภาพรวมค่าใช้จ่าย",
  };
  const applied = appliedByTab[tabValue];
  const cd = dashboardCost && dashboardCost.costData;
  const realCostData = cd && (cd.totalSalary || cd.totalOT || cd.totalNet) ? cd : null;
  const empLoading = !(vendorUsers && vendorUsers.active);
  const costLoading = !dashboardCost;
  const appliedMonthText = applied
    ? dayjs().month(applied.month - 1).format("MMMM")
    : "";
  const appliedYearBE = applied ? applied.year + 543 : "";
  const exportFileName = applied
    ? `${tabLabels[tabValue]}_${appliedMonthText}_${appliedYearBE}`
    : tabLabels[tabValue];
  const tabDataLoaded = {
    overviewAdmin: !!dashboardOverview,
    dashboardOT: !!dashboardOvertime,
    dashboardWorkingTime: !!dashboardWorkingtime,
    dashboardCost: !!dashboardCost,
  };
  const currentTabLoaded = !!applied && !!tabDataLoaded[tabValue];

  return (
    <StyledRoot className="page">
      <Container
        maxWidth="lg"
        style={{
          marginTop: "20px",
          height: "fit-content",
          minHeight: "calc(100vh - 100px)",
          paddingBottom: "40px",
        }}
      >
        <Paper
          elevation={2}
          style={{ padding: "16px", marginBottom: "24px", borderRadius: "16px" }}
        >
          <Grid container spacing={2} alignItems="flex-start">
            {/* แถวบน: ปี + เดือน ชิดขวา */}
            <Grid item xs={12} container spacing={2} justifyContent="flex-end">
              <Grid item xs={6} sm={3}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel>{t("Year")}</InputLabel>
                  <Select
                    label={`${t("Year")}`}
                    displayEmpty
                    disableUnderline
                    value={year}
                    onChange={(event) => {
                      if (
                        dayjs()
                          .set("month", month - 1)
                          .set("year", parseInt(event.target.value))
                          .isBefore(dayjs().set("month", 1).set("year", 2023))
                      ) {
                        setMonth(2);
                      } else if (
                        dayjs()
                          .set("month", month - 1)
                          .set("year", parseInt(event.target.value))
                          .isAfter(dayjs())
                      ) {
                        setMonth(dayjs().get("month") + 1);
                      }
                      setYear(event.target.value);
                    }}
                  >
                    {[...Array(dayjs().format("YYYY") - 2023 + 1)].map(
                      (_, index) => (
                        <MenuItem
                          key={index}
                          value={Number(
                            dayjs().subtract(index, "year").format("YYYY")
                          )}
                        >
                          {parseInt(dayjs().format("YYYY")) - index}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel>{t("Month")}</InputLabel>
                  <Select
                    label={`${t("Month")}`}
                    displayEmpty
                    disableUnderline
                    value={month}
                    onChange={(e) => {
                      setMonth(e.target.value);
                    }}
                  >
                    {[...Array(12)].map((_, index) => {
                      if (
                        dayjs()
                          .set("month", index)
                          .set("year", year)
                          .isSameOrAfter(
                            dayjs().set("month", 1).set("year", 2023)
                          ) &&
                        !dayjs()
                          .set("month", index)
                          .set("year", year)
                          .isAfter(dayjs())
                      ) {
                        return (
                          <MenuItem key={index} value={index + 1}>
                            {dayjs().month(index).format("MMMM")}
                          </MenuItem>
                        );
                      }
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* แผนก (Section) + ส่วน (Department) */}
            <Grid item xs={12} sm={6}>
              <FormControl variant="filled" fullWidth>
                <InputLabel shrink>{t("Section")}</InputLabel>
                <Select
                  label={`${t("Section")}`}
                  displayEmpty
                  disableUnderline
                  value={department}
                  onChange={(e) => {
                    setDepartment(e.target.value);
                    setSection("");
                  }}
                >
                  <MenuItem value={""}>{t("All")}</MenuItem>
                  {departmentStore &&
                    departmentStore.map((d) => (
                      <MenuItem key={d.idDepartment} value={d.idDepartment}>
                        {d.departmentName}{" "}
                        {d.divisionName ? `(${d.divisionName})` : null}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl variant="filled" fullWidth>
                <InputLabel shrink>{t("Department")}</InputLabel>
                <Select
                  label={`${t("Department")}`}
                  notched
                  displayEmpty
                  disableUnderline
                  value={section}
                  onChange={(e) => {
                    setSection(e.target.value);
                  }}
                >
                  <MenuItem value={""}>{t("All")}</MenuItem>
                  {departmentStore &&
                    department &&
                    (
                      (departmentStore.find((d) => d.idDepartment === department) ||
                        {}).sections || []
                    ).map((s) => (
                      <MenuItem key={s.idSection} value={s.idSection}>
                        {s.sectionName}{" "}
                        {s.sectionName ? `(${s.sectionName})` : null}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* ปุ่มค้นหา ชิดขวาล่าง */}
            <Grid item xs={12} container justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{
                  borderRadius: "8px",
                  px: 4,
                  backgroundColor: "#007afe",
                  "&:hover": { backgroundColor: "#1565c0" },
                }}
              >
                {t("Search")}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Box className="tabs-container">
          <TabsStyled
            value={tabValue}
            onChange={(event, newValue) => handleChangeTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab
              className="tab TabBorderRight"
              icon={<DashboardIcon />}
              iconPosition="start"
              label="มุมมองภาพรวม"
              value="overviewAdmin"
            />
            <Tab
              className="tab TabBorderRight"
              icon={<Filter1Icon />}
              iconPosition="start"
              label="ข้อมูลค่าล่วงเวลา"
              value="dashboardOT"
            />
            <Tab
              className="tab TabBorderRight"
              icon={<Filter2Icon />}
              iconPosition="start"
              label="ภาพรวมเวลาทำงาน"
              value="dashboardWorkingTime"
            />
            <Tab
              className="tab"
              icon={<Filter3Icon />}
              iconPosition="start"
              label="ภาพรวมค่าใช้จ่าย"
              value="dashboardCost"
            />
          </TabsStyled>
        </Box>

        {!appliedByTab[tabValue] && (
          <Box>
            <Typography textAlign="center" color="text.secondary">
              {t("PleaseSearchData")}
            </Typography>
          </Box>
        )}

        {applied && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography color="text.secondary" sx={{ fontSize: 14 }}>
              {t("Month")}: <b>{appliedMonthText}</b> &nbsp;|&nbsp; {t("Year")}:{" "}
              <b>{appliedYearBE}</b>
            </Typography>
            {currentTabLoaded && (
              <ExportButton
                getElement={() => contentRef.current}
                fileName={exportFileName}
                title={tabLabels[tabValue]}
                subtitle={`${t("Month")}: ${appliedMonthText}  |  ${t(
                  "Year"
                )}: ${appliedYearBE}`}
              />
            )}
          </Box>
        )}

        <Box ref={contentRef}>
        {appliedByTab.overviewAdmin && tabValue === "overviewAdmin" && (
          <Box>
            {dashboardOverview ? (
              <OverviewPanel
                month={appliedByTab.overviewAdmin.month}
                year={appliedByTab.overviewAdmin.year}
                realEmployee={computeRealEmployee(appliedByTab.overviewAdmin)}
                realCost={realCostData}
                realOT={realCostData ? realCostData.totalOT : null}
                realWorkingTime={computeRealWorkingTime(appliedByTab.overviewAdmin)}
                workdayRows={computeWorkdayRows(appliedByTab.overviewAdmin)}
                top5LeaveAbsent={computeWorkdayTop5Absent(appliedByTab.overviewAdmin)}
                resignList={resignList}
                newEmployees={computeNewEmployees(appliedByTab.overviewAdmin)}
                fetchOT36={() => fetchOT36List(appliedByTab.overviewAdmin)}
                activeEmpMap={activeEmpMap}
                headcountTrend={computeHeadcountTrend(appliedByTab.overviewAdmin)}
                empLoading={empLoading}
                costLoading={costLoading}
              />
            ) : (
              <Typography textAlign="center">{t("LoadingData")}...</Typography>
            )}
          </Box>
        )}
        {appliedByTab.dashboardOT && tabValue === "dashboardOT" && (
          <Box>
            {dashboardOvertime ? (
              <OvertimePanel
                month={appliedByTab.dashboardOT.month}
                year={appliedByTab.dashboardOT.year}
                realEmployee={computeRealEmployee(appliedByTab.dashboardOT)}
                realCost={realCostData}
                realOT={realCostData ? realCostData.totalOT : null}
                fetchOT36={() => fetchOT36List(appliedByTab.dashboardOT)}
                activeEmpMap={activeEmpMap}
                headcountByDept={computeHeadcountByDept(appliedByTab.dashboardOT)}
                empLoading={empLoading}
                costLoading={costLoading}
              />
            ) : (
              <Typography textAlign="center">{t("LoadingData")}...</Typography>
            )}
          </Box>
        )}
        {appliedByTab.dashboardWorkingTime && tabValue === "dashboardWorkingTime" && (
          <Box>
            {dashboardWorkingtime ? (
              <WorkingtimePanel
                month={appliedByTab.dashboardWorkingTime.month}
                year={appliedByTab.dashboardWorkingTime.year}
                realEmployee={computeRealEmployee(appliedByTab.dashboardWorkingTime)}
                realWorkingTime={computeRealWorkingTime(appliedByTab.dashboardWorkingTime)}
                leaveByType={computeLeaveByType(appliedByTab.dashboardWorkingTime)}
                empLoading={empLoading}
              />
            ) : (
              <Typography textAlign="center">{t("LoadingData")}...</Typography>
            )}
          </Box>
        )}
        {appliedByTab.dashboardCost && tabValue === "dashboardCost" && (
          <Box>
            {dashboardCost ? (
              <CostPanel
                month={appliedByTab.dashboardCost.month}
                year={appliedByTab.dashboardCost.year}
                realEmployee={computeRealEmployee(appliedByTab.dashboardCost)}
                realOT={realCostData ? realCostData.totalOT : null}
                empLoading={empLoading}
              />
            ) : (
              <Typography textAlign="center">{t("LoadingData")}...</Typography>
            )}
          </Box>
        )}
        </Box>
      </Container>
    </StyledRoot>
  );
};

export default Overview;
